import { FormEvent, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { ApiError, CurrentUser, api } from '../lib/api';

type Props = {
  user: CurrentUser;
  onUserChanged: (user: CurrentUser) => void;
  onOpenUsers: () => void;
  onLogout: () => Promise<void>;
};

type Dialog = 'profile' | 'password' | undefined;

export function AccountMenu({ user, onUserChanged, onOpenUsers, onLogout }: Props) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog>();
  const [displayName, setDisplayName] = useState(user.displayName);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const hostRef = useRef<HTMLDivElement>(null);

  const roleLabel = user.role === 'ADMIN' ? t('account.admin') : t('account.user');
  const readableError = (reason: unknown) => reason instanceof ApiError
    ? reason.message
    : reason instanceof Error
      ? reason.message
      : t('common.operationFailed');

  useEffect(() => setDisplayName(user.displayName), [user.displayName]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!hostRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  function openDialog(value: Dialog) {
    setOpen(false);
    setDialog(value);
    setError('');
    setNotice('');
  }

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    setWorking(true);
    setError('');
    try {
      const updated = await api<CurrentUser>('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ displayName }),
      });
      onUserChanged(updated);
      setNotice(t('account.profileUpdated'));
    } catch (reason) {
      setError(readableError(reason));
    } finally {
      setWorking(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setError('');
    setNotice('');
    if (passwords.next !== passwords.confirm) {
      setError(t('account.passwordMismatch'));
      return;
    }
    setWorking(true);
    try {
      await api<void>('/auth/password', {
        method: 'PUT',
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.next,
        }),
      });
      setPasswords({ current: '', next: '', confirm: '' });
      setNotice(t('account.passwordUpdated'));
    } catch (reason) {
      setError(readableError(reason));
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <div className="account-menu-host" ref={hostRef}>
        <button className="account-trigger" onClick={() => setOpen((value) => !value)}>
          <span className="account-avatar">{user.displayName.slice(0, 1).toUpperCase()}</span>
          <span className="account-trigger-copy">
            <strong>{user.displayName}</strong>
            <small>{roleLabel}</small>
          </span>
          <span className="account-chevron">⌄</span>
        </button>
        {open && (
          <div className="account-dropdown">
            <div className="account-identity">
              <strong>{user.displayName}</strong>
              <span>@{user.username}</span>
              <small>{user.role === 'ADMIN' ? 'ADMIN' : 'USER'}</small>
            </div>
            <button onClick={() => openDialog('profile')}>{t('account.editCurrent')}</button>
            <button onClick={() => openDialog('password')}>{t('account.changePassword')}</button>
            {user.role === 'ADMIN' && (
              <button onClick={() => { setOpen(false); onOpenUsers(); }}>{t('account.manageUsers')}</button>
            )}
            <div className="account-dropdown-separator" />
            <button className="danger-text" onClick={() => void onLogout()}>{t('account.logout')}</button>
          </div>
        )}
      </div>

      {dialog === 'profile' && (
        <div className="modal-backdrop" onMouseDown={() => setDialog(undefined)}>
          <form className="catalog-modal account-dialog" onSubmit={saveProfile} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">CURRENT USER</p><h3>{t('account.editCurrent')}</h3></div><button type="button" onClick={() => setDialog(undefined)}>{t('common.close')}</button></header>
            {error && <div className="notice error-notice">{error}</div>}
            {notice && <div className="notice success-notice">{notice}</div>}
            <label>{t('account.username')}<input value={user.username} disabled /></label>
            <label>{t('account.role')}<input value={roleLabel} disabled /></label>
            <label>{t('account.displayName')}<input value={displayName} maxLength={128} onChange={(event) => setDisplayName(event.target.value)} required /></label>
            <footer><button type="button" onClick={() => setDialog(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('common.save')}</button></footer>
          </form>
        </div>
      )}

      {dialog === 'password' && (
        <div className="modal-backdrop" onMouseDown={() => setDialog(undefined)}>
          <form className="catalog-modal account-dialog" onSubmit={changePassword} onMouseDown={(event) => event.stopPropagation()}>
            <header><div><p className="eyebrow">CREDENTIALS</p><h3>{t('account.changePassword')}</h3></div><button type="button" onClick={() => setDialog(undefined)}>{t('common.close')}</button></header>
            {error && <div className="notice error-notice">{error}</div>}
            {notice && <div className="notice success-notice">{notice}</div>}
            <label>{t('account.currentPassword')}<input type="password" value={passwords.current} onChange={(event) => setPasswords({ ...passwords, current: event.target.value })} required /></label>
            <label>{t('account.newPassword')}<input type="password" value={passwords.next} onChange={(event) => setPasswords({ ...passwords, next: event.target.value })} required /></label>
            <label>{t('account.confirmPassword')}<input type="password" value={passwords.confirm} onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} required /></label>
            <p className="muted form-hint">{t('account.passwordHint')}</p>
            <footer><button type="button" onClick={() => setDialog(undefined)}>{t('common.cancel')}</button><button className="primary-button" disabled={working}>{t('account.changePassword')}</button></footer>
          </form>
        </div>
      )}
    </>
  );
}
